import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Group,
  Burger,
  Drawer,
  Menu,
  Avatar,
  UnstyledButton,
  Text,
  Indicator,
  Box,
  Stack,
  Divider,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Bell, ChevronDown, LogOut } from "lucide-react";

import Logo from "../../assets/images/logo.png";
import { logout } from "../../features/auth/authActions";
import Api from "../../admin/components/api/Api";
import { setFormData, resetStep } from "../../features/formData/formSlice";
import {
  ADMIN_PANEL_HOME,
  getUserRoles,
  getUserTypeName,
  isAdminUser,
} from "../../admin/navigation/adminNavigation";
import "./MainNavigation.css";

function MainNavigation() {
  const loggedInUser = useSelector((state) => state.auth.user);
  const userType = getUserTypeName(loggedInUser);
  const roles = getUserRoles(loggedInUser);
  const adminUser = isAdminUser(loggedInUser);

  const [userDetail, setUserDetails] = useState(null);
  const [notificationCount, setNotificationCount] = useState(null);
  const [timestamp, setTimestamp] = useState(Date.now());
  const [scrolled, setScrolled] = useState(false);

  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const id = loggedInUser?.user?.id;
  const customerDashboardPath = "/customer/myspace";
  const customerProfilePath = "/customer/profile";
  const dashboardPath = userType === "CUSTOMER" ? customerDashboardPath : "/dashboard";
  const profilePath = userType === "CUSTOMER" ? customerProfilePath : "/user/profile";

  const getDetailsApi = Api({ url: `admin/user/id/${id}` });
  const getNotificationApi = Api({ url: `notify/getCount` });

  useEffect(() => {
    if (id) {
      getDetailsApi
        .fetchData()
        .then((responseData) => setUserDetails(responseData?.data))
        .catch((error) => console.error("Error fetching data:", error));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, timestamp]);

  useEffect(() => {
    const getNotificationData = async () => {
      try {
        const responseData = await getNotificationApi.fetchDataBySingleId();
        if (responseData?.data?.statusType === "SUCCESS") {
          setNotificationCount(responseData.data.totalRecord);
        }
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };
    getNotificationData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTimestamp(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    closeDrawer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logoutHandler = async () => {
    try {
      dispatch(resetStep());
      dispatch(setFormData({}));
      dispatch(logout());
      navigate(adminUser ? "/admin-login" : "/login");
    } catch (error) {
      console.log(error);
    }
  };

  const navLinks = [
    {
      to: dashboardPath,
      label: "Dashboard",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: "/post",
      label: "Posts",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/property",
      label: "Properties",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/promotion-list",
      label: "Promotions",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE", "PARTNER"],
      role: [],
    },
    {
      to: "/query",
      label: "Queries",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE"],
      role: [],
    },
    {
      to: "/help-center",
      label: "Help Center",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: "/user",
      label: "Masters",
      allowedUserTypes: ["SUPER_ADMIN", "ALL_HOME_DESIRE", "PARTNER"],
      role: ["ROLE_ADMIN"],
    },
    {
      to: "my-preference",
      label: "Preference",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
    {
      to: profilePath,
      label: "Profile",
      allowedUserTypes: [
        "SUPER_ADMIN",
        "ALL_HOME_DESIRE",
        "PARTNER",
        "CUSTOMER",
      ],
      role: [],
    },
  ];

  const getFilteredLinks = () =>
    adminUser
      ? [
          { to: ADMIN_PANEL_HOME, label: "Admin Panel" },
          { to: profilePath, label: "Profile" },
        ]
      : navLinks.filter((link) => {
      const isAllowedUser = link.allowedUserTypes.includes(userType);
      const isPartnerAdmin =
        userType === "PARTNER" &&
        roles?.includes("ROLE_ADMIN") &&
        link.allowedUserTypes.includes("PARTNER");
      const isPartnerVisible =
        userType !== "PARTNER" ||
        link.role.length === 0 ||
        roles?.includes("ROLE_ADMIN");
      return (isAllowedUser || isPartnerAdmin) && isPartnerVisible;
    });

  // Transparent ONLY on home page before scroll — solid everywhere else
  const TRANSPARENT_ROUTES = new Set([
    "/",
    "/about-us",
    "/interiordesign",
    "/policy",
    "/contactus",
  ]);

  const isTransparentRoute = TRANSPARENT_ROUTES.has(location.pathname);

  const isTransparent = isTransparentRoute && !scrolled && !drawerOpened;

  const navClass = `main-nav ${
    isTransparent ? "main-nav--transparent" : "main-nav--solid"
  }`;

  return (
    <>
      <header className={navClass}>
        <Container size="xl" h="100%">
          <Group justify="space-between" h="100%">
            <Link to="/" className="main-nav__logo">
              <img src={Logo} alt="Logo" />
            </Link>

            {/* Desktop links */}
            <Group gap={4} visibleFrom="md">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                Home
              </NavLink>
              <NavLink
                to="/about-us"
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                About Us
              </NavLink>

              {/* Services — Interior Design + Property Services (no separate Property link) */}
              <Menu
                trigger="hover"
                openDelay={100}
                closeDelay={400}
                shadow="md"
                width={200}
              >
                <Menu.Target>
                  <UnstyledButton className="main-nav__services-btn">
                    <Group gap={4}>
                      Services <ChevronDown size={14} />
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component={Link} to="/interiordesign">
                    Interior Design
                  </Menu.Item>
                  <Menu.Item component={Link} to="/property-list">
                    Property Services
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <NavLink
                to="/contactus"
                className={({ isActive }) =>
                  `main-nav__link${isActive ? " active" : ""}`
                }
              >
                Contact Us
              </NavLink>
            </Group>

            {/* Right side */}
            <Group gap={8}>
              {loggedInUser?.statusType === "SUCCESS" ? (
                <>
                  <Indicator
                    inline
                    label={notificationCount}
                    size={16}
                    disabled={!notificationCount || notificationCount === 0}
                    color="red"
                    offset={4}
                  >
                    <Link
                      to="/notification"
                      className="main-nav__notification-link"
                    >
                      <Bell size={20} strokeWidth={1.8} />
                    </Link>
                  </Indicator>

                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <UnstyledButton className="main-nav__avatar-btn">
                        <Group gap={8}>
                          <Avatar
                            src={
                              (() => {
                                const photo = userDetail?.user?.photo || userDetail?.photo || userDetail?.image_url || userDetail?.profile_photo || loggedInUser?.user?.photo || loggedInUser?.user?.image_url || loggedInUser?.user?.profile_photo;
                                if (!photo) return null;
                                if (photo.startsWith("http://") || photo.startsWith("https://")) return photo;
                                const base = (userDetail?.assetUrl || loggedInUser?.assetUrl || process.env.REACT_APP_ASSET_URL || "https://images.allhomedesire.in").replace(/\/+$/, "");
                                return `${base}${photo.startsWith("/") ? "" : "/"}${photo}`;
                              })()
                            }
                            radius="xl"
                            size={34}
                            color="blue"
                          >
                            {(userDetail?.user?.first_name || userDetail?.first_name || loggedInUser?.user?.first_name || "C")?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box visibleFrom="sm">
                            <Text className="main-nav__avatar-name">
                              {userDetail?.user?.first_name}
                            </Text>
                          </Box>
                          <ChevronDown size={13} style={{ opacity: 0.5 }} />
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>My Account</Menu.Label>
                      {getFilteredLinks().map((link, index) => (
                        <Menu.Item key={index} component={Link} to={link.to}>
                          {link.label}
                        </Menu.Item>
                      ))}
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<LogOut size={14} />}
                        onClick={logoutHandler}
                      >
                        Logout
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              ) : (
                <Button
                  component={Link}
                  to="/login"
                  variant="filled"
                  size="sm"
                  className="main-nav__login-btn"
                >
                  Log in
                </Button>
              )}

              <Burger
                opened={drawerOpened}
                onClick={toggleDrawer}
                hiddenFrom="md"
                size="sm"
                color={isTransparent ? "#fff" : "#333"}
              />
            </Group>
          </Group>
        </Container>
      </header>

      {/* Mobile drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="md"
        title={<img src={Logo} alt="Logo" style={{ height: "30px" }} />}
        hiddenFrom="md"
        zIndex={1000}
      >
        <Stack gap={0}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            Home
          </NavLink>
          <NavLink
            to="/about-us"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            About Us
          </NavLink>

          <Divider label="Services" labelPosition="center" my="md" />

          <NavLink
            to="/interiordesign"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            Interior Design
          </NavLink>
          <NavLink
            to="/property-list"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            Property Services
          </NavLink>

          <Divider my="md" />

          <NavLink
            to="/contactus"
            className={({ isActive }) =>
              `main-nav__drawer-link${isActive ? " active" : ""}`
            }
            onClick={closeDrawer}
          >
            Contact Us
          </NavLink>
        </Stack>
      </Drawer>
    </>
  );
}

export default MainNavigation;
